import React, { createContext, useContext, useId } from 'react';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import { PRESENCE_HEX_24_PATH } from './presenceIconChrome';

export type PresenceHexOutline = {
	/** Outer half of the border stroke in CSS pixels, before the center stroke is painted. */
	width: number;
	/** Opts the 24px Presence facepile into the updated ADS hexagon geometry. */
	useUpdatedGeometry?: boolean;
	/** Optional refined SVG viewport height in CSS pixels. */
	svgHeight?: number;
	/** Optional center stroke, kept separate from the outer outline. */
	centerStrokeColor?: string;
	centerStrokeWidth?: number;
};

export const PresenceHexOutlineContext: React.Context<PresenceHexOutline | undefined> =
	createContext<PresenceHexOutline | undefined>(undefined);

export const usePresenceSvgHeight = (size: number): number => {
	const outline = useContext(PresenceHexOutlineContext);
	return outline?.svgHeight ?? size;
};

type PresenceHexArtworkProps = {
	d: string;
	fill: string;
	stroke?: string;
	strokeWidth?: number;
	viewBox: string;
	size: number;
	/**
	 * Keeps the whole stroke inside the hexagon instead of centering it on the edge, so the
	 * visible bounding box is the same with and without the border.
	 */
	insetStroke?: boolean;
};

/**
 * Renders a theme outline behind the original fill so its inward half is covered and the fill
 * keeps its original geometry. An optional center stroke is painted separately on top.
 */
export const PresenceHexArtwork = ({
	d,
	fill,
	stroke,
	strokeWidth,
	viewBox,
	size,
	insetStroke = false,
}: PresenceHexArtworkProps): React.JSX.Element => {
	const outline = useContext(PresenceHexOutlineContext);
	const clipPathId = `presence-hex-clip-${useId()}`;
	const [minX, minY, viewBoxWidth, viewBoxHeight] = viewBox.trim().split(/\s+/).map(Number);
	// Keep each brand mark in its existing coordinate system while sharing the 24px tile.
	const useSharedHex =
		outline?.useUpdatedGeometry === true &&
		fg('agentic_presence_facepile_master_gate') &&
		size > 16 &&
		size <= 24 &&
		Number.isFinite(minX) &&
		Number.isFinite(minY) &&
		viewBoxWidth === 24 &&
		viewBoxHeight === 24;
	const path = {
		d: useSharedHex ? PRESENCE_HEX_24_PATH : d,
		transform: useSharedHex
			? `translate(${minX + 0.445} ${minY - 1.5 + 0.62}) scale(${23.11 / 24} ${25.76 / 27})`
			: undefined,
	};
	if (!outline && insetStroke && stroke && strokeWidth) {
		const cssPixelScale =
			Number.isFinite(viewBoxWidth) && viewBoxWidth > 0 ? viewBoxWidth / size : 1;
		// Clipping a doubled stroke to the hexagon leaves only its inner half visible.
		return (
			<>
				<clipPath id={clipPathId}>
					<path {...path} />
				</clipPath>
				<path {...path} fill={fill} />
				<path
					data-presence-layer="inset-stroke"
					{...path}
					fill="none"
					stroke={stroke}
					strokeWidth={strokeWidth * cssPixelScale * 2}
					clipPath={`url(#${clipPathId})`}
				/>
			</>
		);
	}
	if (!outline) {
		return <path {...path} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />;
	}

	if (!Number.isFinite(viewBoxWidth) || viewBoxWidth <= 0) {
		return <path {...path} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />;
	}
	const toSvgStrokeWidth = (cssPixels: number): number => (cssPixels * viewBoxWidth) / size;
	if (outline.width <= 0) {
		return (
			<>
				<path {...path} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />
				{outline.centerStrokeColor && outline.centerStrokeWidth ? (
					<path
						data-presence-layer="center-stroke"
						{...path}
						fill="none"
						stroke={outline.centerStrokeColor}
						strokeWidth={toSvgStrokeWidth(outline.centerStrokeWidth)}
					/>
				) : null}
			</>
		);
	}
	const centerStrokeWidth = outline.centerStrokeColor ? (outline.centerStrokeWidth ?? 0) : 0;
	// A 4px border extends 2px outside the fill. A 1px center stroke covers the first
	// 0.5px, leaving 1.5px of border beyond it. Keep the old compensation behind the gate.
	const backingStrokeWidth = toSvgStrokeWidth(
		outline.width * 2 + (fg('agentic_presence_facepile_master_gate') ? 0 : centerStrokeWidth),
	);

	return (
		<>
			<path
				data-presence-layer="outline"
				{...path}
				fill={stroke}
				stroke={stroke}
				strokeWidth={backingStrokeWidth}
			/>
			<path data-presence-layer="fill" {...path} fill={fill} />
			{outline.centerStrokeColor && outline.centerStrokeWidth ? (
				<path
					data-presence-layer="center-stroke"
					{...path}
					fill="none"
					stroke={outline.centerStrokeColor}
					strokeWidth={toSvgStrokeWidth(outline.centerStrokeWidth)}
				/>
			) : null}
		</>
	);
};
